import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { injectIntl } from 'react-intl';
import Helmet from 'helmet/helmet';
import { URLBuilder } from 'url-builder/url-builder';

import media from 'lib/media/media';
import SvgIcon from 'lib/images/svg-icon/svg-icon';
import observeOnce from 'lib/intersection-observer/observe-once';
import PhotoswipeGallery from 'lib/images/photoswipe-gallery-view';

import { SiteContext, UserStoreContext } from 'four-nets';
import { Site } from 'four-nets/site';
import iconGrowth from 'four-nets/icons/svg/icon_growth.svg';
import iconViewsRectangle from 'four-nets/icons/svg/icon_views-rectangle.svg';
import iconProUsers from 'four-nets/icons/svg/icon_pro_users.svg';
import Footer from 'four-nets/footer/footer';
import HeaderView, { HeaderViewModel } from 'four-nets/header/header-view';

import query, { VendorReferencesQuery } from './vendor-references.graphql';
import defaultStyles from './landing-page.scss';

interface Props {
    styles: typeof defaultStyles;
    intl?: any;
    site: Site;
}

const IMAGE_SIZES = [
    { w: 1647, h: 864 },
    { w: 1727, h: 867 },
    { w: 1734, h: 892 },
    { w: 1647, h: 862 },
    { w: 1759, h: 996 },
];

const COUNTS = {
    101: [120000, 500000, 10000],
    102: [140000, 800000, 13000],
    301: [400000, 3000000, 200000],
    302: [200000, 1300000, 70000],
    401: [200000, 400000, 10000],
};

const _LandingPage: React.FC<Omit<Props, 'site'>> = props => {
    const { styles, intl } = props;

    const site = useContext(SiteContext);
    const userStore = useContext(UserStoreContext);

    const bgImg = useMemo(() => {
        return require(`./${site.id}/bg.jpeg`);
    }, [site]);

    useEffect(() => {
        import('four-nets/lib/google-analytics/google-analytics')
            .then(({ default: loadGA }) => loadGA({ media }))
            .catch(console.error);
    }, []);

    const headerViewModel = useMemo(
        () =>
            new HeaderViewModel({
                userStore,
                urlBuilder: URLBuilder.defaultURLBuilder,
                site,
                intl,
            }),
        [userStore, site, intl]
    );

    const renderHelmet = (H: any) => {
        return (
            <FormattedMessage id='PRO_LANDING_HTML_TITLE'>
                {msg => (
                    <FormattedMessage id='PRO_LANDING_HTML_DESCRIPTION'>
                        {msg2 => (
                            <H>
                                <title>
                                    {msg} | {site.name}{' '}
                                </title>
                                <meta name='description' content={msg2 as string} />
                                <meta property='og:description' content={msg2 as string} />
                            </H>
                        )}
                    </FormattedMessage>
                )}
            </FormattedMessage>
        );
    };

    return (
        <>
            <Helmet render={renderHelmet} />
            <header>
                <HeaderView headerViewModel={headerViewModel} />
            </header>
            <div className={styles.wrapper}>
                <div className={styles.backgroundImage} style={{ backgroundImage: `url(${bgImg})` }}>
                    <div className={styles.titleContainer}>
                        <h1 className={styles.title}>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_TITLE_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_TITLE_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_TITLE' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_TITLE_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_TITLE_401' />
                            ) : null}
                        </h1>
                    </div>
                </div>
                <Section1 styles={styles} site={site} />
                <Section2 styles={styles} site={site} />
                <Section3 styles={styles} site={site} />
                <Section4 styles={styles} site={site} />
                <Section5 styles={styles} site={site} />
            </div>
            <Footer style={{ margin: styles.footerMargin }} />
        </>
    );
};

const LandingPage = injectIntl(_LandingPage);

const Section1: React.FC<Props> = ({ styles, site }) => (
    <div className={styles.section1}>
        <h2 className={styles.primaryText}>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_S1_TITLE_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_S1_TITLE_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_S1_TITLE' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_S1_TITLE_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_S1_TITLE_401' />
            ) : null}
        </h2>
        <div className={styles.secondaryText}>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_S1_SUBTITLE_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_S1_SUBTITLE_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_S1_SUBTITLE' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_S1_SUBTITLE_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_S1_SUBTITLE_401' />
            ) : null}
        </div>
        <a href='/pro/' className={styles.registerButton}>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_BUTTON_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_BUTTON_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_BUTTON' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_BUTTON_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_BUTTON_401' />
            ) : null}
        </a>
        <div className={styles.price}>
            {site.price} {site.currency} / {site.isSlovak ? 'mesačne' : 'měsíčně'}
        </div>
        <div className='text-xs mt-4'>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_S1_VAT_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_S1_VAT_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_S1_VAT' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_S1_VAT_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_S1_VAT_401' />
            ) : null}
        </div>
        <div className={styles.disclaimer}>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_S1_DISCOUNT_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_S1_DISCOUNT_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_S1_DISCOUNT' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_S1_DISCOUNT_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_S1_DISCOUNT_401' />
            ) : null}
        </div>
    </div>
);

const Section2: React.FC<Props> = ({ styles, site }) => {
    const [countAnimationsRunning, setCountAnimationsRunning] = useState(false);
    const [appear, setAppear] = useState(false);

    const ref = useRef<HTMLDivElement>(null);
    const timeout = useRef<NodeJS.Timeout | null>(null);
    const disconnect = useRef<(() => void) | null>(null);

    const refDidAppear = () => {
        setCountAnimationsRunning(true);
        timeout.current = setTimeout(() => setAppear(true), 1500);
    };

    useEffect(() => {
        if (ref.current) {
            disconnect.current = observeOnce(ref.current, refDidAppear, { rootMargin: '-5px' });
        }

        return () => {
            if (disconnect.current) {
                disconnect.current();
            }

            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        };
    }, []);

    return (
        <div ref={ref} className={styles.section2}>
            <div className={styles.statTile}>
                <SvgIcon src={iconGrowth} className={styles.icon} />
                <div className={styles.count1}>
                    <Counter finalNumber={COUNTS[site.id][0]} running={countAnimationsRunning} />
                    <span className={classnames(styles.plus, { [styles.appear]: appear })}> +</span>
                </div>
                <div className={styles.label}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL1_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL1_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL1' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL1_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL1_401' />
                    ) : null}
                </div>
            </div>
            <div className={styles.statTile}>
                <SvgIcon src={iconViewsRectangle} className={styles.icon} />
                <div className={styles.count2}>
                    <Counter finalNumber={COUNTS[site.id][1]} running={countAnimationsRunning} />
                </div>

                <div className={styles.label}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL2_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL2_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL2' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL2_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL2_401' />
                    ) : null}
                </div>
            </div>
            <div className={styles.statTile}>
                <SvgIcon src={iconProUsers} className={styles.icon} />
                <div className={styles.count3}>
                    <Counter finalNumber={COUNTS[site.id][2]} running={countAnimationsRunning} />
                </div>
                <div className={styles.label}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL3_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL3_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL3' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL3_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S2_LABEL3_401' />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const Section3: React.FC<Props> = ({ styles, site }) => {
    const images = useMemo(() => {
        const _img = (i: number) => require(`./${site.id}/img${i}.png`);
        const _images = [_img(1), _img(2), _img(3), _img(4)];
        if (site.isWedding) {
            _images.push(_img(5));
        }
        return _images;
    }, [site]);

    return (
        <div className={styles.section3}>
            <h2 className={styles.coloredTitle}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S3_TITLE_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S3_TITLE_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S3_TITLE' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S3_TITLE_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S3_TITLE_401' />
                ) : null}
            </h2>
            <p className={styles.text}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE1_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE1_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE1' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE1_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE1_401' />
                ) : null}
            </p>
            <p className={styles.text}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE2_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE2_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE2' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE2_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S3_SUBTITLE2_401' />
                ) : null}
            </p>
            <div className={styles.infoRow}>
                <PhotoswipeGallery
                    style={{ photoswipeGallery: styles.photoswipeGallery }}
                    items={[images[0]]}
                    photoSwipeItem={(src: Img) => ({
                        src: src,
                        msrc: src,
                        ...IMAGE_SIZES[0],
                    })}
                    itemElement={<GalleryItem site={site} styles={styles} />}
                />
                <div className={styles.info}>
                    <h3 className={styles.infoTitle}>
                        {site.id === 101 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE_101' />
                        ) : site.id === 102 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE_102' />
                        ) : site.id === 301 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE' />
                        ) : site.id === 302 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE_302' />
                        ) : site.id === 401 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE_401' />
                        ) : null}
                    </h3>
                    <ul>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM1_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM1_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM1' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM1_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM1_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM2_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM2_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM2' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM2_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM2_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM3_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM3_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM3' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM3_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM3_401' />
                            ) : null}
                        </li>
                    </ul>
                </div>
            </div>

            <div className={styles.infoRow}>
                <div className={styles.info}>
                    <h3 className={styles.infoTitle}>
                        {site.id === 101 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE2_101' />
                        ) : site.id === 102 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE2_102' />
                        ) : site.id === 301 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE2' />
                        ) : site.id === 302 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE2_302' />
                        ) : site.id === 401 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE2_401' />
                        ) : null}
                    </h3>
                    <ul>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM4_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM4_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM4' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM4_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM4_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM5_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM5_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM5' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM5_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM5_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM6_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM6_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM6' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM6_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM6_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM7_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM7_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM7' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM7_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM7_401' />
                            ) : null}
                        </li>
                    </ul>
                </div>
                <PhotoswipeGallery
                    style={{ photoswipeGallery: styles.photoswipeGallery }}
                    items={[images[1]]}
                    photoSwipeItem={(src: Img) => ({
                        src: src,
                        msrc: src,
                        ...IMAGE_SIZES[1],
                    })}
                    itemElement={<GalleryItem site={site} styles={styles} />}
                />
            </div>

            <div className={styles.infoRow}>
                <PhotoswipeGallery
                    style={{ photoswipeGallery: styles.photoswipeGallery }}
                    items={[images[2]]}
                    photoSwipeItem={(src: Img) => ({
                        src: src,
                        msrc: src,
                        ...IMAGE_SIZES[2],
                    })}
                    itemElement={<GalleryItem site={site} styles={styles} />}
                />
                <div className={styles.info}>
                    <h3 className={styles.infoTitle}>
                        {site.id === 101 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE3_101' />
                        ) : site.id === 102 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE3_102' />
                        ) : site.id === 301 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE3' />
                        ) : site.id === 302 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE3_302' />
                        ) : site.id === 401 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE3_401' />
                        ) : null}
                    </h3>
                    <ul>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM8_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM8_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM8' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM8_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM8_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM9_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM9_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM9' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM9_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM9_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM10_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM10_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM10' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM10_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM10_401' />
                            ) : null}
                        </li>
                    </ul>
                </div>
            </div>

            <div className={styles.infoRow}>
                <div className={styles.info}>
                    <h3 className={styles.infoTitle}>
                        {site.id === 101 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE4_101' />
                        ) : site.id === 102 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE4_102' />
                        ) : site.id === 301 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE4' />
                        ) : site.id === 302 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE4_302' />
                        ) : site.id === 401 ? (
                            <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE4_401' />
                        ) : null}
                    </h3>
                    <ul>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM11_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM11_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM11' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM11_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM11_401' />
                            ) : null}
                        </li>
                        <li>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM12_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM12_102' />
                            ) : site.id === 301 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM12' />
                            ) : site.id === 302 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM12_302' />
                            ) : site.id === 401 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM12_401' />
                            ) : null}
                        </li>
                    </ul>
                </div>
                <PhotoswipeGallery
                    style={{ photoswipeGallery: styles.photoswipeGallery }}
                    items={[images[3]]}
                    photoSwipeItem={(src: Img) => ({
                        src: src,
                        msrc: src,
                        ...IMAGE_SIZES[3],
                    })}
                    itemElement={<GalleryItem site={site} styles={styles} />}
                />
            </div>
            {site.isWedding ? (
                <div className={styles.infoRow}>
                    <PhotoswipeGallery
                        style={{ photoswipeGallery: styles.photoswipeGallery }}
                        items={[images[4]]}
                        photoSwipeItem={(src: Img) => ({
                            src: src,
                            msrc: src,
                            ...IMAGE_SIZES[4],
                        })}
                        itemElement={<GalleryItem site={site} styles={styles} />}
                    />
                    <div className={styles.info}>
                        <h3 className={styles.infoTitle}>
                            {site.id === 101 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE5_101' />
                            ) : site.id === 102 ? (
                                <FormattedMessage id='PRO_LANDING_S3_INFO_TITLE5_102' />
                            ) : null}
                        </h3>
                        <ul>
                            <li>
                                {site.id === 101 ? (
                                    <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM13_101' />
                                ) : site.id === 102 ? (
                                    <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM13_102' />
                                ) : null}
                            </li>
                            <li>
                                {site.id === 101 ? (
                                    <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM14_101' />
                                ) : site.id === 102 ? (
                                    <FormattedMessage id='PRO_LANDING_S3_INFO_ITEM14_102' />
                                ) : null}
                            </li>
                        </ul>
                    </div>
                </div>
            ) : null}

            <a href='/pro/' className={styles.registerButton}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_401' />
                ) : null}
            </a>
        </div>
    );
};

const Section4: React.FC<Props> = ({ styles, site }) => (
    <div className={styles.section4}>
        <h2 className={styles.coloredTitle}>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_S4_TITLE_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_S4_TITLE_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_S4_TITLE' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_S4_TITLE_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_S4_TITLE_401' />
            ) : null}
        </h2>
        <p className={styles.text}>
            {site.id === 101 ? (
                <FormattedMessage id='PRO_LANDING_S4_SUBTITLE_101' />
            ) : site.id === 102 ? (
                <FormattedMessage id='PRO_LANDING_S4_SUBTITLE_102' />
            ) : site.id === 301 ? (
                <FormattedMessage id='PRO_LANDING_S4_SUBTITLE' />
            ) : site.id === 302 ? (
                <FormattedMessage id='PRO_LANDING_S4_SUBTITLE_302' />
            ) : site.id === 401 ? (
                <FormattedMessage id='PRO_LANDING_S4_SUBTITLE_401' />
            ) : null}
        </p>
        <div className={styles.ulRow}>
            <ul>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION1_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION1_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION1' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION1_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION1_401' />
                    ) : null}
                </li>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION2_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION2_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION2' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION2_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION2_401' />
                    ) : null}
                </li>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION3_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION3_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION3' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION3_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION3_401' />
                    ) : null}
                </li>
            </ul>
            <ul>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION4_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION4_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION4' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION4_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION4_401' />
                    ) : null}
                </li>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION5_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION5_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION5' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION5_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION5_401' />
                    ) : null}
                </li>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION6_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION6_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION6' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION6_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION6_401' />
                    ) : null}
                </li>
            </ul>
            <ul>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION7_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION7_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION7' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION7_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION7_401' />
                    ) : null}
                </li>
                <li>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION8_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION8_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION8' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION8_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S4_PROFESSION8_401' />
                    ) : null}
                </li>
            </ul>
        </div>
    </div>
);

const Section5: React.FC<Props> = ({ styles, site }) => {
    const { data } = useQuery<VendorReferencesQuery>(query);

    return (
        <div className={styles.section5}>
            <h2 className={styles.coloredTitle}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE1_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE1_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE1' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE1_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE1_401' />
                ) : null}
            </h2>
            <div className={styles.cards}>
                {data?.vendorReferences?.edges &&
                    data.vendorReferences.edges.map(edge => (
                        <div className={styles.card}>
                            <div className={styles.feedback}>{edge?.node?.text}</div>
                            <div className={styles.avatarWrapper}>
                                <img
                                    src={edge?.node?.vendor.user.profile.avatar?.src}
                                    alt='img'
                                    className={styles.avatar}
                                />
                            </div>
                            <div className={styles.vendorName}>{edge?.node?.name}</div>
                            <Link to={edge?.node?.vendor.href as string} className={styles.vendorLink}>
                                {edge?.node?.vendor.name}
                            </Link>
                        </div>
                    ))}
            </div>

            <h2 className={styles.coloredTitle}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE2_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE2_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE2' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE2_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE2_401' />
                ) : null}
            </h2>

            <div className={styles.QandA}>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q1_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q1_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q1' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q1_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q1_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A1_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A1_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A1' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A1_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A1_401' />
                    ) : null}
                </div>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q2_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q2_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q2' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q2_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q2_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A2_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A2_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A2' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A2_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A2_401' />
                    ) : null}
                </div>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q3_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q3_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q3' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q3_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q3_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A3_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A3_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A3' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A3_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A3_401' />
                    ) : null}
                </div>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q4_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q4_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q4' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q4_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q4_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A4_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A4_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A4' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A4_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A4_401' />
                    ) : null}
                </div>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q5_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q5_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q5' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q5_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q5_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A5_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A5_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A5' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A5_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A5_401' />
                    ) : null}
                </div>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q6_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q6_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q6' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q6_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q6_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A6_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A6_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A6' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A6_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A6_401' />
                    ) : null}
                </div>
                <div className={styles.question}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q7_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q7_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q7' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q7_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_Q7_401' />
                    ) : null}
                </div>
                <div className={styles.answer}>
                    {site.id === 101 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A7_101' />
                    ) : site.id === 102 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A7_102' />
                    ) : site.id === 301 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A7' />
                    ) : site.id === 302 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A7_302' />
                    ) : site.id === 401 ? (
                        <FormattedMessage id='PRO_LANDING_S5_A7_401' />
                    ) : null}
                </div>
            </div>

            <h2 className={classnames(styles.coloredTitle, styles.margin)}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE3_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE3_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE3' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE3_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S5_TITLE3_401' />
                ) : null}
            </h2>
            <div className={styles.contactRow}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_401' />
                ) : null}
            </div>
            <div className={styles.contactRow}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_EMAIL_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_EMAIL_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_EMAIL' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_EMAIL_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_EMAIL_401' />
                ) : null}
            </div>
            <div className={styles.contactRow}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_TEL_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_TEL_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_TEL' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_TEL_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_S5_SUPPORT_TEL_401' />
                ) : null}
            </div>

            <a href='/pro/' className={styles.registerButton}>
                {site.id === 101 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_101' />
                ) : site.id === 102 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_102' />
                ) : site.id === 301 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON' />
                ) : site.id === 302 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_302' />
                ) : site.id === 401 ? (
                    <FormattedMessage id='PRO_LANDING_BUTTON_401' />
                ) : null}
            </a>
        </div>
    );
};

const GalleryItem: React.FC<Props & { item?: any }> = props => {
    const { styles, item } = props;
    const { onClick, src } = item;

    return <img src={src} className={styles.image} alt='img1' onClick={onClick} />;
};

const Counter: React.FC<{ finalNumber: number; running: boolean; secondsDuration?: number }> = props => {
    const { finalNumber, secondsDuration = 2 } = props;

    const requestRef = React.useRef<number>();

    const [count, setCount] = React.useState(0);

    const frameRate = 60;
    const aroundOneSecondInFrames = (frameRate - 1) * secondsDuration;
    const iteration = finalNumber / aroundOneSecondInFrames;

    const animate = () => {
        if (count <= finalNumber) {
            setCount(prevCount => Math.min(finalNumber, prevCount + iteration));
        }
    };

    React.useEffect(() => {
        if (props.running) {
            requestRef.current = requestAnimationFrame(animate);
        }
        return () => cancelAnimationFrame(requestRef.current!);
    }, [count, props.running]);

    const formattedCount = Math.round(count).toLocaleString().replace(/,/g, ' ');

    return <>{formattedCount}</>;
};

export default Styled(defaultStyles)(LandingPage);
